import { useState } from "react";
import { ConfigProvider, Drawer, Input } from "antd";
import { dataTableTheme } from "@/components/data-table/dataTableTheme";
import { SOVEREIGN } from "@/features/epc/utils/epcProjectVisual";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../services/authService";
import { toast } from "react-hot-toast";

// Sovereign Emerald palette (card shell — same register as UserInfoCard)
const SE = {
  paper:         '#FAF7F0',
  raised:        'rgba(255,253,246,0.97)',
  hairline:      'rgba(11,59,46,0.14)',
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  champagne:     '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneTile: 'rgba(201,169,97,0.20)',
  champagneRing: 'rgba(201,169,97,0.45)',
  muted:         '#7C8A82',
  ink:           '#2A1F17',
};

const EMPTY_FORM = { currentPassword: "", newPassword: "", confirmPassword: "" };

const PASSWORD_RULE = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;

/** Field label — tracked-caps register, same as the drawer forms elsewhere. */
const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <label
    className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-[0.14em]"
    style={{ color: SOVEREIGN.title }}
  >
    {children}
  </label>
);

const PasswordField = ({
  label, value, onChange, error, placeholder, autoComplete, onPressEnter,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder: string;
  autoComplete: string;
  onPressEnter?: () => void;
}) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Input.Password
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      status={error ? "error" : undefined}
      style={{ background: SOVEREIGN.inputBg, borderColor: error ? undefined : SOVEREIGN.inputBorder }}
      onPressEnter={onPressEnter}
    />
    {error && (
      <p className="mt-1.5 text-xs font-medium" style={{ color: "#B91C1C" }}>
        {error}
      </p>
    )}
  </div>
);

/** Primary register button (flat engraved green). */
const PrimaryButton = ({
  children, onClick, loading,
}: {
  children: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="inline-flex items-center gap-2 rounded-xl px-5 py-2 text-[12.5px] font-bold text-white transition-colors disabled:opacity-50"
    style={{ background: SOVEREIGN.btn, border: `1px solid ${SOVEREIGN.btnBorder}`, boxShadow: SOVEREIGN.btnShadow }}
    onMouseEnter={(e) => { e.currentTarget.style.background = SOVEREIGN.btnHover; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = SOVEREIGN.btn; }}
  >
    {loading && (
      <span
        aria-hidden
        className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
      />
    )}
    {children}
  </button>
);

/** Secondary register button (ivory, hairline). */
const SecondaryButton = ({
  children, onClick, disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-xl px-4 py-2 text-[12.5px] font-bold transition-colors disabled:opacity-50"
    style={{ background: SOVEREIGN.inputBg, border: `1px solid ${SOVEREIGN.inputBorder}`, color: SOVEREIGN.title }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = SOVEREIGN.inputHoverBorder; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = SOVEREIGN.inputBorder; }}
  >
    {children}
  </button>
);

export default function SecurityCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<typeof EMPTY_FORM>>({});

  // Only an explicit false means OTP-only; undefined (stale cache) shows the form.
  const otpOnly = user?.hasPassword === false;

  const setField = (field: keyof typeof EMPTY_FORM) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<typeof EMPTY_FORM> = {};
    if (!form.currentPassword) next.currentPassword = "Enter your current password";
    if (!form.newPassword) {
      next.newPassword = "Enter a new password";
    } else if (!PASSWORD_RULE.test(form.newPassword)) {
      next.newPassword = "At least 8 characters, with at least one letter and one number";
    } else if (form.newPassword === form.currentPassword) {
      next.newPassword = "New password must be different from the current password";
    }
    if (form.confirmPassword !== form.newPassword) {
      next.confirmPassword = "Passwords do not match";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setOpen(false);
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const result = await authService.changePassword(form.currentPassword, form.newPassword);
      if (result.success) {
        toast.success("Password changed successfully");
        handleClose();
      } else if (/current password/i.test(result.message)) {
        setErrors({ currentPassword: result.message });
      } else {
        toast.error(result.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const drawerFooter = (
    <div className="flex items-center justify-end gap-3">
      <SecondaryButton onClick={handleClose} disabled={saving}>Cancel</SecondaryButton>
      <PrimaryButton onClick={handleSave} loading={saving}>
        {saving ? "Changing…" : "Change Password"}
      </PrimaryButton>
    </div>
  );

  return (
    <div
      className="rounded-2xl p-5 lg:p-6"
      style={{
        backgroundColor: SE.paper,
        border: `1px solid ${SE.hairline}`,
        boxShadow: '1px 0 0 rgba(201,169,97,0.06), 0 4px 16px rgba(11,59,46,0.06)',
      }}
    >
      {/* ── SECTION HEADER ──────────────────────────────────────────── */}
      <div className="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm"
            style={{ backgroundImage: `linear-gradient(135deg, ${SE.bottle}, ${SE.title})` }}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div>
            <h4 className="text-[15px] font-bold tracking-tight" style={{ color: SE.title }}>
              Security
            </h4>
            <p className="text-[11px] mt-0.5" style={{ color: SE.muted }}>
              {otpOnly ? "Sign-in method for this account" : "Manage your account password"}
            </p>
          </div>
        </div>
        {!otpOnly && (
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold transition-all duration-200"
            style={{
              backgroundColor: SE.champagneWash,
              color: SE.bottle,
              border: `1px solid ${SE.champagneRing}`,
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneTile;
              (e.currentTarget as HTMLElement).style.boxShadow = `0 2px 8px rgba(201,169,97,0.20)`;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneWash;
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
            }}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Change Password
          </button>
        )}
      </div>

      {/* Champagne divider */}
      <div className="mb-5 h-px" style={{ background: `linear-gradient(to right, transparent, ${SE.champagneRing}, transparent)` }} />

      {/* ── BODY ─────────────────────────────────────────────────────── */}
      <div
        className="flex items-start gap-3 rounded-xl p-3"
        style={{ backgroundColor: SE.raised, border: `1px solid ${SE.hairline}` }}
      >
        <div
          className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: SE.champagneTile, color: SE.bottle, boxShadow: `inset 0 0 0 1px ${SE.champagneRing}` }}
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {otpOnly ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            )}
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: SE.muted }}>
            {otpOnly ? "Sign-in Method" : "Password"}
          </p>
          <p className="mt-0.5 text-[13px] font-medium" style={{ color: SE.ink }}>
            {otpOnly
              ? "This account signs in with mobile OTP — no password is set."
              : "••••••••"}
          </p>
          <p className="mt-0.5 text-[11px]" style={{ color: SE.muted }}>
            {otpOnly
              ? "Use your registered mobile number and the one-time code to sign in."
              : "Changing your password signs you out on all other devices."}
          </p>
        </div>
      </div>

      {/* ── CHANGE PASSWORD DRAWER ───────────────────────────────────── */}
      <ConfigProvider theme={dataTableTheme}>
        <Drawer
          open={open}
          width={440}
          placement="right"
          destroyOnHidden
          title={<span style={{ color: SOVEREIGN.title }}>Change Password</span>}
          onClose={handleClose}
          footer={drawerFooter}
          styles={{
            header: {
              background: '#F8F2E2',
              borderBottom: '1px solid rgba(11,59,46,0.22)',
            },
            body: {
              padding: 20,
              background: SOVEREIGN.canvas,
            },
            footer: {
              padding: '12px 20px',
              borderTop: '1px solid rgba(11,59,46,0.22)',
              background: 'rgba(250,247,238,0.96)',
            },
          }}
        >
          {/* Emerald info panel */}
          <div
            className="mb-5 flex items-start gap-3 rounded-lg border p-3.5"
            style={{
              borderColor: 'rgba(51,146,116,0.35)',
              background: 'rgba(51,146,116,0.06)',
            }}
          >
            <svg
              className="mt-0.5 h-4.5 w-4.5 flex-shrink-0"
              style={{ color: SOVEREIGN.brand }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <p className="text-[12.5px] leading-relaxed" style={{ color: SOVEREIGN.title }}>
              Your new password must be at least <strong>8 characters</strong> with a letter and
              a number. All other devices will be signed out.
            </p>
          </div>

          <div className="space-y-5">
            <PasswordField
              label="Current Password"
              value={form.currentPassword}
              onChange={setField("currentPassword")}
              error={errors.currentPassword}
              placeholder="Enter current password"
              autoComplete="current-password"
            />

            {/* Hairline rule between current and new */}
            <div className="h-px" style={{ background: SOVEREIGN.innerRule }} />

            <PasswordField
              label="New Password"
              value={form.newPassword}
              onChange={setField("newPassword")}
              error={errors.newPassword}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            <PasswordField
              label="Confirm New Password"
              value={form.confirmPassword}
              onChange={setField("confirmPassword")}
              error={errors.confirmPassword}
              placeholder="Re-enter new password"
              autoComplete="new-password"
              onPressEnter={handleSave}
            />
          </div>
        </Drawer>
      </ConfigProvider>
    </div>
  );
}
